<template>
  <modal
    :name="$lang.messages.changePasswordModal"
    :adaptive="true"
    height="auto"
    :width="500"
    transition="linear"
  >

    <form
      name="changePasswordForm"
      class="fp-modal"
      novalidate
      @submit.prevent="onChangePasswordFormSubmitted"
    >

      <modals-header
        :modalNameProp="$lang.messages.changePasswordModal"
        @hideModalProp="hideChangePasswordModal"
      >
      </modals-header>

      <hr class="v--modal-hr">

      <div class="part">
        <p class="label input-filter-label">
          {{ $lang.messages.currentPassword }}
          <span class="asterix">*</span>
        </p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('changePassword.currentPassword')}">
          <input
            type="password"
            name="currentPassword"
            ref="currentPassword"
            class="input"
            :placeholder="$lang.messages.enterCurrentPassword"
            v-model="userToChangePassword.currentPassword"
            data-vv-scope="changePassword"
            v-validate="'required|min:1|max:64'"
            @click.self="clearInputErrors($event)"
            @focus.self="clearInputErrors($event)"
            @blur="clearOthers($event)"
            @keypress="MX_capsLockHandler($event)"
          />

          <!-- SHOW/HIDE CURRENT PASSWORD -->
          <span class="show-password-icon" @click="MX_togglePassVisibility('currentPassword')">
            <img src="@/assets/images/show-password.png" alt="show password" v-show="MX_showHideCurrentPass">
            <img src="@/assets/images/hide-password.png" alt="hide password" v-show="!MX_showHideCurrentPass">
          </span>

          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
          <div
            v-show="errors.has('changePassword.currentPassword')"
            class="error-tooltip is-input-pass input-pass-modal"
            :class="{'hide-tooltip' : bluredProp !== 'currentPassword'}"
          >
            <div class="text">
              {{ errors.first('changePassword.currentPassword') }}
            </div>
          </div>
        </div>
      </div>

      <div class="part">
        <p class="label input-filter-label">
          {{ $lang.messages.newPassword }}
          <span class="asterix">*</span>
        </p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('changePassword.newPassword')}">
          <input
            type="password"
            name="newPassword"
            class="input"
            ref="newPassword"
            :placeholder="$lang.messages.enterNewPassword"
            v-model="userToChangePassword.newPassword"
            data-vv-scope="changePassword"
            v-validate="{
              required: true,
              min: 1,
              max: 64,
              regex: /^(?=.*?[a-z])(?=.*?[0-9]).{8,64}$/
            }"
            @click.self="clearInputErrors($event)"
            @focus.self="clearInputErrors($event)"
            @blur="clearOthers($event)"
            @keypress="MX_capsLockHandler($event)"
          />

          <!-- SHOW/HIDE PASSWORD -->
          <span class="show-password-icon" @click="MX_togglePassVisibility('newPassword')">
            <img src="@/assets/images/show-password.png" alt="show password" v-show="MX_showHidePassword">
            <img src="@/assets/images/hide-password.png" alt="hide password" v-show="!MX_showHidePassword">
          </span>

          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
          <div
            v-show="errors.has('changePassword.newPassword')"
            class="error-tooltip is-input-pass input-pass-modal"
            :class="{'hide-tooltip' : bluredProp !== 'newPassword'}"
          >
            <ul class="text">
              <li>{{$lang.messages.passMsgLabel}}:</li>
              <li>
                {{$lang.messages.passMsgLength}}
              </li>
              <li>
                {{$lang.messages.passMsgLowercase}}
              </li>
              <li>
                {{$lang.messages.passMsgNumber}}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="part">
        <p class="label input-filter-label">
          {{ $lang.messages.confirmPassword }}
          <span class="asterix">*</span>
        </p>
        <div class="input-wrapper input-field" :class="{'error-input' : errors.has('changePassword.confirmPassword')}">
          <input
            type="password"
            name="confirmPassword"
            ref="repeatPassword"
            class="input"
            :placeholder="$lang.messages.enterConfirmPassword"
            v-model="userToChangePassword.confirmPassword"
            data-vv-scope="changePassword"
            v-validate="'required|confirmed:newPassword|min:1|max:64'"
            @click.self="clearInputErrors($event)"
            @focus.self="clearInputErrors($event)"
            @blur="clearOthers($event)"
            @keypress="MX_capsLockHandler($event)"
          />

          <!-- SHOW/HIDE REPEAT PASSWORD -->
          <span class="show-password-icon" @click="MX_togglePassVisibility('repeatPassword')">
            <img src="@/assets/images/show-password.png" alt="show password" v-show="MX_showHideRepeatPass">
            <img src="@/assets/images/hide-password.png" alt="hide password" v-show="!MX_showHideRepeatPass">
          </span>

          <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon" />
          <div
            v-show="errors.has('changePassword.confirmPassword')"
            class="error-tooltip"
            :class="{'hide-tooltip' : bluredProp !== 'confirmPassword'}"
          >
            <div class="text">
              {{ errors.first('changePassword.confirmPassword') }}
            </div>
          </div>
        </div>
      </div>

      <!-- CAPS LOCK HANDLER -->
      <div v-if="MX_isCapsLockOn" class="caps-lock-wrapper">
        <span>{{ $lang.messages.capsIsOn }}</span>
      </div>

      <div class="part">
        <p class="label input-filter-label">
          <span class="asterix">*</span> {{ $lang.messages.mandatoryFields }}
        </p>
      </div>

      <button
        type="button"
        class="generic-btn"
        @click="hideChangePasswordModal"
      >
        {{ $lang.messages.back }}
      </button>
      <button
        type="submit"
        class="action-btn"
      >
        <span v-show="!loading">{{ $lang.messages.changePassword }}</span>
        <ClipLoader :loading="loading" :color="'#fff'" :size="'20px'"></ClipLoader>
      </button>
    </form>
  </modal>
</template>

<script>
import { mapActions } from "vuex";
import { CHANGE_PASSWORD } from "@/store/action.types";
import {isCapsOn} from "@/mixins/isCapsOn";
import modalsHeader from "@/components/common/modals/ModalsHeader";
import {showHidePassword} from "@/mixins/showHidePassword";
import ClipLoader from 'vue-spinner/src/ClipLoader';
import STATUS_CODES from "@/common/statusCodes";

export default {
  name: "changePassword",
  inject: ['$validator'],
  components: {
    ClipLoader,
    'modals-header': modalsHeader
  },
  mixins: [
    isCapsOn,
    showHidePassword
  ],
  props: {},
  data() {
    return {
      userToChangePassword: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      },
      bluredProp: '',
      loading: false,
    };
  },
  updated() {
    this.setMarginTooltip()
  },
  methods: {
    ...mapActions({
      changePasswordAction: CHANGE_PASSWORD
    }),
    show() {
      this.errors.clear();
      this.clearChangePasswordData();
      this.$modal.show(this.$lang.messages.changePasswordModal);
    },
    hideChangePasswordModal() {
      this.checkAnyInput();
      this.$modal.hide(this.$lang.messages.changePasswordModal);
    },
    clearChangePasswordData() {
      this.userToChangePassword.currentPassword = ''
      this.userToChangePassword.newPassword = ''
      this.userToChangePassword.confirmPassword = ''
      this.$validator.reset()
      this.checkAnyInput()
    },
    onChangePasswordFormSubmitted() {
      if (!this.loading) {
        this.setMarginTooltip();
        this.checkAnyInput();
        this.$validator.validateAll('changePassword').then(result => {
          if (result) {
            this.loading = true;
            this.changePasswordAction({
              oldKey: this.userToChangePassword.currentPassword,
              newKey: this.userToChangePassword.newPassword
            })
            .then(() => {
              this.serverErrors = false;
              this.loading = false;
              this.hideChangePasswordModal();
              this.$snotify.success(this.$lang.messages.sn_successPassChanged, "SUCCESS");
            })
            .catch(error => {
              this.loading = false;
              if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
                this.$snotify.error(error.response.data.status.desc, "ERROR");
              }
            });
          }
        });
      }
    },
    setMarginTooltip() {
      let errorTooltip = document.getElementsByClassName('error-tooltip')

      for (let item of errorTooltip) {
        let height = item.offsetHeight
        if (height >= 60) {
          item.style.marginTop = "-26%"
        } else {
          item.style.marginTop = "-85px"
        }
      }
    },
    clearInputErrors(e) {
      this.$validator.errors.remove('changePassword.' + e.target.name)
    },
    checkAnyInput() {
      this.bluredProp = ''
    },
    clearOthers(e) {
      this.bluredProp = e.target.name;
      let errorMsg = this.bluredProp;
      if (!e.target.value || e.target.name === "") {
        if (errorMsg === 'currentPassword') {
          errorMsg = 'Current Password'
        } else if (errorMsg === 'newPassword') {
          errorMsg ='New Password';
        } else {
          errorMsg = 'Confirmed Password'
        }
        this.$validator.errors.add({
          field: this.bluredProp,
          scope: 'changePassword',
          msg: `${this.$lang.messages.vee_the} ${errorMsg} ${this.$lang.messages.vee_requiredField}.`
        })
      }
    }
  }
};
</script>
